import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChefHat, 
  FileText, 
  Scale, 
  Shield, 
  AlertTriangle, 
  CheckCircle,
  XCircle,
  Clock,
  CreditCard,
  Truck,
  Users,
  Phone,
  Mail
} from 'lucide-react';

const TermsOfService = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const lastUpdated = "December 15, 2024";

  const sections = [
    {
      id: 'acceptance',
      title: 'Acceptance of Terms',
      icon: CheckCircle,
      color: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
      content: [
        {
          subtitle: 'Agreement to Terms',
          text: 'By accessing and using B.Tech Bawarchi\'s food delivery services, you accept and agree to be bound by the terms and provision of this agreement.',
          items: [
            'You must be at least 18 years old to use our services',
            'You agree to provide accurate and complete information',
            'You are responsible for maintaining the confidentiality of your account',
            'You agree to use our services only for lawful purposes'
          ]
        },
        {
          subtitle: 'Modifications',
          text: 'We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting.',
          items: [
            'Continued use of our services constitutes acceptance of modified terms',
            'We will notify users of significant changes via email or app notification',
            'You can review the current terms at any time on our website',
            'If you disagree with changes, you must discontinue use of our services'
          ]
        }
      ]
    },
    {
      id: 'services',
      title: 'Our Services',
      icon: ChefHat,
      color: 'linear-gradient(135deg, #f97316 0%, #ef4444 100%)',
      content: [
        {
          subtitle: 'Food Delivery Services',
          text: 'B.Tech Bawarchi provides food delivery services through multiple channels:',
          items: [
            'Direct ordering through our website and mobile app',
            'Third-party delivery platforms (Zomato, Swiggy)',
            'Phone orders and customer service support',
            'Bulk orders and catering services for events'
          ]
        },
        {
          subtitle: 'Service Availability',
          text: 'Our services are subject to availability and may be limited by:',
          items: [
            'Geographic delivery areas and coverage',
            'Operating hours (typically 9:00 AM - 11:00 PM)',
            'Menu item availability and seasonal changes',
            'Weather conditions and force majeure events'
          ]
        }
      ]
    },
    {
      id: 'orders',
      title: 'Orders and Payments',
      icon: CreditCard,
      color: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      content: [
        {
          subtitle: 'Order Placement',
          text: 'When placing an order, you agree to:',
          items: [
            'Provide accurate delivery address and contact information',
            'Specify any dietary restrictions or special instructions',
            'Confirm order details before final submission',
            'Be available to receive the order at the specified time'
          ]
        },
        {
          subtitle: 'Payment Terms',
          text: 'Payment for orders must be made through approved methods:',
          items: [
            'Cash on delivery (COD) for eligible orders',
            'Online payment through secure payment gateways',
            'UPI, credit/debit cards, and digital wallets',
            'Payment must be completed before order processing'
          ]
        },
        {
          subtitle: 'Pricing and Charges',
          text: 'Order pricing includes:',
          items: [
            'Food item prices as displayed on our menu',
            'Applicable taxes and service charges',
            'Delivery fees based on distance and location',
            'Any applicable discounts or promotional offers'
          ]
        }
      ]
    },
    {
      id: 'delivery',
      title: 'Delivery and Cancellation',
      icon: Truck,
      color: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      content: [
        {
          subtitle: 'Delivery Process',
          text: 'We strive to deliver orders within the estimated time frame:',
          items: [
            'Standard delivery time: 25-35 minutes',
            'Peak hours may extend delivery time to 45 minutes',
            'Delivery personnel will contact you upon arrival',
            'You must be present to receive the order'
          ]
        },
        {
          subtitle: 'Cancellation Policy',
          text: 'Orders can be cancelled under the following conditions:',
          items: [
            'Cancellation within 5 minutes of order placement',
            'Kitchen has not started preparing your order',
            'Cancellation due to item unavailability',
            'Force majeure events beyond our control'
          ]
        },
        {
          subtitle: 'Refund Policy',
          text: 'Refunds will be processed in the following cases:',
          items: [
            'Order cancellation before preparation begins',
            'Quality issues or incorrect items delivered',
            'Significant delay beyond promised delivery time',
            'Payment processing errors or duplicate charges'
          ]
        }
      ]
    },
    {
      id: 'user-responsibilities',
      title: 'User Responsibilities',
      icon: Users,
      color: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
      content: [
        {
          subtitle: 'Account Security',
          text: 'You are responsible for:',
          items: [
            'Maintaining the security of your account credentials',
            'All activities that occur under your account',
            'Notifying us immediately of any unauthorized access',
            'Keeping your contact information up to date'
          ]
        },
        {
          subtitle: 'Prohibited Activities',
          text: 'You agree not to:',
          items: [
            'Use our services for any illegal or unauthorized purpose',
            'Attempt to gain unauthorized access to our systems',
            'Interfere with or disrupt our services or servers',
            'Use automated systems to access our services'
          ]
        },
        {
          subtitle: 'Feedback and Reviews',
          text: 'When providing feedback, you agree to:',
          items: [
            'Provide honest and constructive feedback',
            'Not post false, misleading, or defamatory content',
            'Respect other users and our staff members',
            'Follow our community guidelines and standards'
          ]
        }
      ]
    },
    {
      id: 'liability',
      title: 'Limitation of Liability',
      icon: Shield,
      color: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      content: [
        {
          subtitle: 'Service Limitations',
          text: 'Our liability is limited in the following ways:',
          items: [
            'We are not liable for delays due to traffic or weather',
            'Food quality issues must be reported within 2 hours',
            'Maximum liability is limited to the order value',
            'We are not responsible for third-party delivery services'
          ]
        },
        {
          subtitle: 'Force Majeure',
          text: 'We are not liable for circumstances beyond our control:',
          items: [
            'Natural disasters and extreme weather conditions',
            'Government regulations and policy changes',
            'Technical failures and system outages',
            'Supplier issues and ingredient unavailability'
          ]
        }
      ]
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual Property',
      icon: FileText,
      color: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      content: [
        {
          subtitle: 'Our Content',
          text: 'All content on our platform is protected by intellectual property laws:',
          items: [
            'Menu descriptions, images, and branding materials',
            'Website design, layout, and user interface',
            'Recipes, cooking methods, and food preparation techniques',
            'Software, algorithms, and proprietary technology'
          ]
        },
        {
          subtitle: 'User Content',
          text: 'By submitting content to us, you grant us:',
          items: [
            'Non-exclusive license to use your feedback and reviews',
            'Right to display user-generated content on our platform',
            'Permission to use content for marketing and promotional purposes',
            'Right to modify or remove content that violates our terms'
          ]
        }
      ]
    },
    {
      id: 'privacy',
      title: 'Privacy and Data Protection',
      icon: Scale,
      color: 'linear-gradient(135deg, #a855f7 0%, #9333ea 100%)',
      content: [
        {
          subtitle: 'Data Collection',
          text: 'We collect and process personal data in accordance with our Privacy Policy:',
          items: [
            'Personal information for order processing and delivery',
            'Payment information for transaction processing',
            'Location data for delivery optimization',
            'Usage data for service improvement and analytics'
          ]
        },
        {
          subtitle: 'Data Security',
          text: 'We implement appropriate security measures to protect your data:',
          items: [
            'SSL encryption for all data transmission',
            'Secure payment processing with PCI DSS compliance',
            'Regular security audits and vulnerability assessments',
            'Employee training on data protection practices'
          ]
        }
      ]
    }
  ];

  const contactInfo = [
    {
      icon: Phone,
      title: 'Customer Service',
      details: ['+91 8107436861', 'Mon-Sun: 9:00 AM - 11:00 PM']
    },
    {
      icon: Mail,
      title: 'Legal Inquiries',
      details: ['legal@btechbawarchi.com', 'support@btechbawarchi.com']
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f8f9fa 100%)',
      color: '#333',
      fontFamily: 'Inter, sans-serif'
    }}>
      {/* Header */}
      <header style={{
        background: 'white',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 50
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              padding: '12px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #f97316 0%, #ef4444 50%, #ec4899 100%)'
            }}>
              <ChefHat size={24} color="white" />
            </div>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#1f2937',
              margin: 0
            }}>B.Tech Bawarchi</h1>
          </div>
          <nav style={{ display: 'flex', gap: '24px' }}>
            <Link to="/" style={{ color: '#6b7280', textDecoration: 'none', fontWeight: '500' }}>Home</Link>
            <Link to="/about" style={{ color: '#6b7280', textDecoration: 'none', fontWeight: '500' }}>About</Link>
            <Link to="/contact" style={{ color: '#6b7280', textDecoration: 'none', fontWeight: '500' }}>Contact</Link>
            <Link to="/menu" style={{ color: '#6b7280', textDecoration: 'none', fontWeight: '500' }}>Menu</Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section style={{
        padding: '60px 16px',
        textAlign: 'center',
        background: 'linear-gradient(135deg, rgba(251, 115, 22, 0.05) 0%, rgba(239, 68, 68, 0.05) 100%)'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          transition: 'all 1s ease',
          opacity: isLoaded ? 1 : 0,
          transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #f97316 0%, #ef4444 100%)',
              boxShadow: '0 10px 20px rgba(251, 115, 22, 0.3)'
            }}>
              <Scale size={40} color="white" />
            </div>
          </div>
          <h2 style={{
            fontSize: '48px',
            fontWeight: '900',
            color: '#1f2937',
            margin: '0 0 16px 0',
            letterSpacing: '-0.025em',
            lineHeight: '1.1'
          }}>
            Terms of{' '}
            <span style={{
              background: 'linear-gradient(135deg, #f97316 0%, #ef4444 50%, #dc2626 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Service
            </span>
          </h2>
          <p style={{
            fontSize: '20px',
            color: '#6b7280',
            lineHeight: '1.6',
            margin: '0 0 24px 0'
          }}>
            Please read these terms carefully before using our food delivery services. 
            These terms govern your use of B.Tech Bawarchi's platform and services.
          </p>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'white',
            padding: '8px 16px',
            borderRadius: '20px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <Clock size={16} color="#6b7280" />
            <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>
              Last updated: {lastUpdated}
            </span>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section style={{
        padding: '60px 16px',
        background: 'white'
      }}>
        <div style={{
          maxWidth: '1000px',
          margin: '0 auto',
          transition: 'all 1s ease 0.2s',
          opacity: isLoaded ? 1 : 0,
          transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            borderRadius: '16px',
            padding: '32px',
            border: '1px solid #f59e0b',
            marginBottom: '40px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <AlertTriangle size={24} color="#d97706" />
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#92400e',
                margin: 0
              }}>
                Important Legal Notice
              </h3>
            </div>
            <p style={{
              fontSize: '16px',
              color: '#92400e',
              lineHeight: '1.6',
              margin: 0
            }}>
              These Terms of Service constitute a legally binding agreement between you and B.Tech Bawarchi. 
              By using our services, you acknowledge that you have read, understood, and agree to be bound by these terms.
            </p>
          </div>
          
          <div style={{
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            borderRadius: '16px',
            padding: '32px',
            border: '1px solid #bae6fd',
            marginBottom: '40px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <CheckCircle size={24} color="#0ea5e9" />
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#0c4a6e',
                margin: 0
              }}>
                Your Rights and Obligations
              </h3>
            </div>
            <p style={{
              fontSize: '16px',
              color: '#0c4a6e',
              lineHeight: '1.6',
              margin: 0
            }}>
              These terms outline your rights as a customer and your obligations when using our services. 
              We are committed to providing transparent and fair terms for all our users.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section style={{
        padding: '60px 16px',
        background: 'linear-gradient(135deg, #f9fafb 0%, #ffffff 100%)'
      }}>
        <div style={{
          maxWidth: '1000px',
          margin: '0 auto'
        }}>
          {sections.map((section, index) => (
            <div
              key={section.id}
              style={{
                background: 'white',
                borderRadius: '20px',
                padding: '40px',
                marginBottom: '40px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                transition: `all 1s ease ${0.4 + index * 0.1}s`,
                opacity: isLoaded ? 1 : 0,
                transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '32px'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: section.color,
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)'
                }}>
                  <section.icon size={28} color="white" />
                </div>
                <h3 style={{
                  fontSize: '28px',
                  fontWeight: '800',
                  color: '#1f2937',
                  margin: 0
                }}>
                  {section.title}
                </h3>
              </div>
              
              {section.content.map((content, contentIndex) => (
                <div key={contentIndex} style={{ marginBottom: '32px' }}>
                  <h4 style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#374151',
                    margin: '0 0 16px 0'
                  }}>
                    {content.subtitle}
                  </h4>
                  <p style={{
                    fontSize: '16px',
                    color: '#6b7280',
                    lineHeight: '1.6',
                    margin: '0 0 16px 0'
                  }}>
                    {content.text}
                  </p>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0
                  }}>
                    {content.items.map((item, itemIndex) => (
                      <li key={itemIndex} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        marginBottom: '12px',
                        fontSize: '16px',
                        color: '#6b7280',
                        lineHeight: '1.6'
                      }}>
                        <div style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: '#f97316',
                          marginTop: '8px',
                          flexShrink: 0
                        }}></div>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section style={{
        padding: '60px 16px',
        background: 'white'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          textAlign: 'center',
          transition: 'all 1s ease 1.2s',
          opacity: isLoaded ? 1 : 0,
          transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
        }}>
          <h3 style={{
            fontSize: '32px',
            fontWeight: '800',
            color: '#1f2937',
            margin: '0 0 16px 0'
          }}>
            Questions About These Terms?
          </h3>
          <p style={{
            fontSize: '18px',
            color: '#6b7280',
            lineHeight: '1.6',
            margin: '0 0 40px 0'
          }}>
            If you have any questions about these Terms of Service or need clarification 
            on any provision, please contact our legal team.
          </p>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(2, 1fr)',
            gap: '32px',
            marginBottom: '40px'
          }}>
            {contactInfo.map((info, index) => (
              <div
                key={index}
                style={{
                  background: 'linear-gradient(135deg, #f9fafb 0%, #ffffff 100%)',
                  borderRadius: '16px',
                  padding: '24px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                  <info.icon size={24} color="#f97316" />
                  <h4 style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#1f2937',
                    margin: 0
                  }}>
                    {info.title}
                  </h4>
                </div>
                {info.details.map((detail, idx) => (
                  <p key={idx} style={{
                    fontSize: '16px',
                    color: '#6b7280',
                    margin: '0 0 8px 0',
                    lineHeight: '1.5'
                  }}>
                    {detail}
                  </p>
                ))}
              </div>
            ))}
          </div>
          
          <div style={{
            background: 'linear-gradient(135deg, #f97316 0%, #ef4444 100%)',
            borderRadius: '16px',
            padding: '32px',
            color: 'white'
          }}>
            <h4 style={{
              fontSize: '20px',
              fontWeight: '700',
              margin: '0 0 16px 0'
            }}>
              Legal Compliance
            </h4>
            <p style={{
              fontSize: '16px',
              opacity: 0.9,
              lineHeight: '1.6',
              margin: 0
            }}>
              These terms are governed by Indian law and any disputes will be subject to 
              the jurisdiction of courts in Jaipur, India.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        background: '#1f2937',
        color: 'white',
        padding: '48px 16px 24px 16px'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: '24px'
          }}>
            <div style={{
              padding: '8px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #f97316 0%, #ef4444 50%, #ec4899 100%)'
            }}>
              <ChefHat size={24} color="white" />
            </div>
            <h4 style={{
              fontSize: '20px',
              fontWeight: '700',
              margin: 0
            }}>B.Tech Bawarchi</h4>
          </div>
          <p style={{
            color: '#9ca3af',
            marginBottom: '24px',
            lineHeight: '1.6',
            fontSize: '14px'
          }}>
            Serving authentic Indian cuisine with love and tradition since 2019.
          </p>
          <div style={{
            borderTop: '1px solid #374151',
            paddingTop: '16px',
            color: '#9ca3af',
            fontSize: '12px'
          }}>
            <p style={{ margin: 0 }}>&copy; 2024 B.Tech Bawarchi. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
